import { supabase } from './supabase'
import type { Space } from './session'

/**
 * The two numbers a space carries, and what they actually control.
 *
 * Both have existed since the first migration and neither has ever been
 * settable. `default_lead_days` decides where a bill lands in the heavy-day
 * view — how far ahead of its due date it is expected to be paid. The green
 * lane ceiling decides how much money the app is allowed to wave through
 * without a person looking at it, which is the single most consequential
 * setting in the product and the one most deserving of a screen.
 */

export interface SpaceSettings {
  leadDays: number
  greenLaneCeilingCents: number
}

export async function loadSpaceSettings(spaceId: string): Promise<SpaceSettings> {
  const { data, error } = await supabase
    .from('spaces')
    .select('default_lead_days, green_lane_ceiling_cents')
    .eq('id', spaceId)
    .maybeSingle()
  if (error) throw error

  return {
    leadDays: Number(data?.default_lead_days ?? 4),
    greenLaneCeilingCents: Number(data?.green_lane_ceiling_cents ?? 100000),
  }
}

export async function saveSpaceSettings(
  space: Space,
  settings: SpaceSettings,
  userId: string,
): Promise<void> {
  // Bounds here as well as in the form: a lead time of 400 days would quietly
  // move every bill in the house into next year.
  const leadDays = Math.min(30, Math.max(0, Math.round(settings.leadDays)))
  const ceiling = Math.max(0, Math.round(settings.greenLaneCeilingCents))

  const { error } = await supabase
    .from('spaces')
    .update({ default_lead_days: leadDays, green_lane_ceiling_cents: ceiling })
    .eq('id', space.id)
  if (error) throw error

  await supabase.from('activity_log').insert({
    space_id: space.id,
    actor_id: userId,
    action: 'space.settings_changed',
    entity_type: 'space',
    entity_id: space.id,
    detail: { lead_days: leadDays, green_lane_ceiling_cents: ceiling },
  })
}
